"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, CardContent, Divider, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import type { ClauseRow } from "../types";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type Props = {
  title?: string;
  rows: ClauseRow[];
  groupField?: "clause_group" | "clause_hr";
  palette?: string[];
  textColor?: string;
  colorByRank?: boolean;
  onCategoryClick?: (category: string) => void;
};

type ClauseGroupField = "clause_group" | "clause_hr";

type ClauseGroupDistributionItem = {
  name: string;
  value: number;
  proportionPercent: number;
};

const CLAUSE_GROUP_DISPLAY_NAME_MAP: Record<string, string> = {
  Consequences: "Consequences of Default",
  "Events of Default and Other Triggers; Adverse Consequences":
    "Events of Default and Other Triggers",
  Confidentiality: "Confidentiality and Disclosure",
  Indebtedness: "Indebtedness (inc. Financial, External, Public External)",
};

export function getClauseGroupDisplayName(name: string): string {
  return CLAUSE_GROUP_DISPLAY_NAME_MAP[name] ?? name;
}

const DEFAULT_PALETTE = [
  "#6366f1",
  "#ec4899",
  "#f97316",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ef4444",
  "#14b8a6",
  "#d97706",
];

export function getClauseGroupDistribution(
  rows: ClauseRow[],
  groupField: ClauseGroupField = "clause_group",
): {
  items: ClauseGroupDistributionItem[];
  totalDocs: number;
} {
  const counts: Record<string, number> = {};
  const groupDocSets: Record<string, Set<string>> = {};

  rows.forEach((row) => {
    const value = row[groupField];
    if (!value) {
      return;
    }

    const cleanedValue = String(value).replace(/^"|"$/g, "").trim();
    if (!cleanedValue) {
      return;
    }

    if (groupField === "clause_group") {
      const docId = String(row.DOCID ?? "").trim();
      if (!docId) {
        return;
      }

      if (!groupDocSets[cleanedValue]) {
        groupDocSets[cleanedValue] = new Set<string>();
      }
      groupDocSets[cleanedValue].add(docId);
    } else {
      counts[cleanedValue] = (counts[cleanedValue] ?? 0) + 1;
    }
  });

  if (groupField === "clause_group") {
    Object.keys(groupDocSets).forEach((group) => {
      counts[group] = groupDocSets[group].size;
    });
  }

  const totalDocs = Object.values(counts).reduce((sum, val) => sum + val, 0);

  const items = Object.entries(counts)
    .map(([name, value]) => ({
      name,
      value,
      proportionPercent: totalDocs > 0 ? (value / totalDocs) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);

  return { items, totalDocs };
}

export default function ClauseGroupTreemap({
  title = "Clauses by Group",
  rows,
  groupField = "clause_group",
  palette = DEFAULT_PALETTE,
  textColor = "white",
  colorByRank = false,
  onCategoryClick,
}: Props) {
  const option = useMemo<EChartsOption>(() => {
    // const { items, totalDocs } = getClauseGroupDistribution(rows, groupField);
    const { items } = getClauseGroupDistribution(rows, groupField);

    // Convert to array, calculate proportions, and sort by count descending
    const data = items
      .map(({ name, value, proportionPercent }, rankIndex) => {
        let colorIndex: number;

        if (colorByRank) {
          // Largest tile index 0 (darkest), smallest last index (lightest)
          colorIndex = Math.min(
            Math.floor(
              (rankIndex / Math.max(items.length - 1, 1)) *
                (palette.length - 1),
            ),
            palette.length - 1,
          );
        } else {
          // Map proportion to palette index
          colorIndex = 5; // default
          if (proportionPercent < 4) {
            colorIndex = 5; // lightest
          } else if (proportionPercent < 7) {
            colorIndex = 4;
          } else if (proportionPercent <= 8) {
            colorIndex = 3;
          } else if (proportionPercent < 10) {
            colorIndex = 2;
          } else if (proportionPercent < 11) {
            colorIndex = 1;
          } else if (proportionPercent < 20 && items.length <= 4) {
            colorIndex = 4; // lighter blue for the smallest square in "Bond" only selection
          } else if (proportionPercent < 20) {
            colorIndex = 0; // darkest blue in top level chart when selecting all
          } else if (proportionPercent < 30) {
            colorIndex = 7;
          } else if (proportionPercent < 40 && items.length <= 4) {
            colorIndex = 0; // darker blue for the largest squares in "Bond" and "Loan" selection
          } else if (proportionPercent < 40) {
            colorIndex = 8;
          } else if (proportionPercent < 50 && items.length <= 4) {
            colorIndex = 0; // darker blue for the largest squares in "Bond" only selection
          } else if (proportionPercent < 50) {
            colorIndex = 6;
          } else {
            colorIndex = 2;
          }
        }

        return {
          name,
          value,
          itemStyle: {
            color: palette[Math.min(colorIndex, palette.length - 1)],
          },
        };
      })
      .sort((a, b) => (b.value as number) - (a.value as number));

    return {
      tooltip: {
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number };
          const displayName = getClauseGroupDisplayName(p.name ?? "");
          const docCount = p.value ?? 0;
          // const proportion = totalDocs > 0 ? (docCount / totalDocs) * 100 : 0;
          // return `${displayName}<br/><b>${docCount} documents</b><br/>(${proportion.toFixed(1)}%)`;
          return `${displayName}<br/><b>${docCount} documents</b>`;
        },
      },
      series: [
        {
          type: "treemap",
          data,
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          visibleMin: 200,
          label: {
            show: true,
            fontSize: 12,
            fontWeight: "600",
            color: textColor,
            lineHeight: 15,
            overflow: "break",
            padding: 8,
            formatter: (params: unknown) => {
              const p = params as { name?: string; value?: number };
              return `${getClauseGroupDisplayName(p.name ?? "")}\n(${p.value ?? 0})`;
            },
          },
          // Use each tile's rendered rectangle size to improve text wrapping
          labelLayout: (params: {
            rect?: { width?: number; height?: number };
          }) => {
            const rawWidth = params.rect?.width ?? 0;
            const rawHeight = params.rect?.height ?? 0;

            const tileArea = rawWidth * rawHeight;
            if (tileArea < 2500 || rawWidth < 50) {
              return {
                width: 0,
                height: 0,
                fontSize: 0,
                lineHeight: 0,
                overflow: "break",
              };
            }

            const pad = tileArea < 5000 ? 4 : 10;

            const width = Math.max(28, Math.floor(rawWidth - pad * 2));
            const height = Math.max(20, Math.floor(rawHeight - pad * 2));
            const area = width * height;

            return {
              width,
              height,
              fontSize:
                width < 200 && area < 30000 ? 10 : area > 100000 ? 12 : 11,
              lineHeight: width < 200 ? 10 : 12,
              overflow: "break",
            };
          },
          itemStyle: {
            gapWidth: 3,
            borderWidth: 0,
          },
          emphasis: { disabled: true },
        },
      ],
    } as EChartsOption;
  }, [colorByRank, groupField, palette, rows, textColor]);

  const events = useMemo(
    () => ({
      click: (params: {
        data?: { name?: string; value?: number };
        event?: {
          target?: {
            getBoundingRect?: () => {
              width?: number;
              height?: number;
            };
          };
        };
      }) => {
        const clicked = params?.data?.name;
        const rect = params?.event?.target?.getBoundingRect?.();
        const width = Number(rect?.width ?? 0);
        const height = Number(rect?.height ?? 0);
        const area = width * height;

        console.log("[ClauseGroupTreemap] clicked tile metrics", {
          name: clicked ?? "Unknown",
          width,
          height,
          area,
        });

        if (clicked) {
          onCategoryClick?.(clicked);
        }
      },
    }),
    [onCategoryClick],
  );

  return (
    // <Card sx={{ px: "1rem", py: "1rem" }}>
    <CardContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 0,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#363636" }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <ReactECharts
        option={option}
        style={{ height: 360, width: "100%" }}
        onEvents={events}
        notMerge
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
        }}
      >
        <Box
          component="img"
          src="/SDF-Logo.png"
          alt="Sovereign Debt Forum logo"
          sx={{
            width: { xs: 92, sm: 110 },
            height: "auto",
            flexShrink: 0,
            opacity: 0.7,
          }}
        />
        <Typography
          sx={{
            mt: 1,
            fontSize: "0.7rem",
            color: "#626367",
            textAlign: "left",
            lineHeight: "1rem",
          }}
        >
          This data reflects the documents available on our platform, not all
          sovereign debt.
        </Typography>
      </Box>
    </CardContent>
    // </Card>
  );
}
